import { useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { PrimaryButton, SecondaryButton } from 'components/Button';
import { Image } from 'components/Image/Image';
import { Body } from 'components/Text/Body';
import { BodyLarge } from 'components/Text/BodyLarge';
import { BodyMicro } from 'components/Text/BodyMicro';
import { CmsApiFlashCard as FlashCardType } from 'modules/cms/api/types/blocks/CmsApiFlashCardQuizBlock';
import { colors, cssBreakpoints, transition } from 'theme';
import { imageUrl } from 'utils/filestack';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledImage = styled(({ className, ...props }: any) => (
  <Box className={className}>
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <Image {...props} />
  </Box>
))`
  img {
    display: block;
    margin: 0 auto;
    max-height: 300px;
    max-width: 100%;
    @supports (object-fit: contain) {
      width: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  cursor: pointer;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  cursor: pointer;
`;

const Card = styled(Box)`
  transform-style: preserve-3d;
`;

const CardContainer = styled(Box)<{ $flipped?: boolean }>`
  perspective: 1000px;
  cursor: pointer;
  ${(props) => `
  &:hover {
    ${Card} {
      transform: rotateY(${props.$flipped ? 183 : 3}deg);
    }
    ${PrimaryButton} {
      background: ${colors.brandGreen};
    }
    ${SecondaryButton} {
      background: white;
      color: ${colors.brandBlue};
    }
    @media all and (min-width: ${cssBreakpoints.smUp}) {
      ${Card} {
        transform: rotateX(${props.$flipped ? 183 : 3}deg);
      }
    }
  }`}
`;

const CardTile = styled(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading, @typescript-eslint/no-explicit-any
  (props: any) => <Box p={[24, null, 48]} {...props} />,
)`
  backface-visibility: hidden;
  width: 100%;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const CardBack = styled(CardTile)`
  margin-left: -100%;
  background: ${colors.brandBlue};
  color: white;
`;

const CardFront = styled(CardTile)`
  transform: translateZ(0);
  background: white;
`;

type Props = {
  blockIndex: number;
  card: FlashCardType;
  index: number;
};

export function CmsFlashCardQuizBlockCard({
  blockIndex,
  card: { question, answer, image, instructions },
  index,
}: Props) {
  const [flipped, setFlipped] = useState(false);

  const flip = () => setFlipped(!flipped);

  const Question = question.length > 80 ? Body : BodyLarge;
  const Answer = answer.length > 280 ? Body : BodyLarge;
  return (
    // @ts-expect-error TS(2322): Type 'false | (number | null)[]' is not assignable... Remove this comment to see the full error message
    <Box mt={index > 0 && [32, null, 64]}>
      <CardContainer
        width="calc(100% + 8px)"
        mt={24}
        ml="-4px"
        p="4px"
        textAlign="center"
        $flipped={flipped}
      >
        <Card
          transform={[
            `translateZ(0) ${flipped ? 'rotateY(180deg)' : ''}`,
            null,
            `translateZ(0) ${flipped ? 'rotateX(180deg)' : ''}`,
          ]}
          display="flex"
          width={1}
          alignItems="stretch"
          transition={`all ${transition}`}
          onClick={flip}
        >
          <CardFront>
            <BodyMicro color={colors.lightContentGrey} mb={18}>
              {index + 1}. {instructions}
            </BodyMicro>
            <StyledImage
              src={imageUrl({
                data: image,
                width: undefined,
                height: undefined,
                fit: 'max',
                insecureSVG: false,
              })}
              alt={question}
            />
            <Question
              analyticsData={{
                eventName: 'CMS Block',
                eventProperties: {
                  block_position: blockIndex,
                  block_type: 'Flash Card Quiz',
                },
              }}
              mt={36}
              dangerouslySetInnerHTML={{
                __html: question,
              }}
            />
            <Box mt={20}>
              <StyledPrimaryButton
                size="small"
                onClick={flip}
                tabIndex={flipped ? -1 : 0}
              >
                {getText('Reveal Answer')}
              </StyledPrimaryButton>
            </Box>
          </CardFront>
          <CardBack transform={['rotateY(180deg)', null, 'rotateX(180deg)']}>
            <Box component={BodyMicro} flex="0 0 auto" opacity={0.7} mb={20}>
              {index + 1}. {instructions}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flex="1 1 auto"
            >
              <Answer
                analyticsData={{
                  eventName: 'CMS Block',
                  eventProperties: {
                    block_position: blockIndex,
                    block_type: 'Flash Card Quiz',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: answer,
                }}
              />
            </Box>
            <Box component={BodyMicro} flex="0 0 auto" mb={20}>
              <StyledSecondaryButton
                size="small"
                onClick={flip}
                tabIndex={flipped ? 0 : -1}
              >
                {getText('Go Back')}
              </StyledSecondaryButton>
            </Box>
          </CardBack>
        </Card>
      </CardContainer>
    </Box>
  );
}
